import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { COPY, LANGS, LangContext } from "@/i18n";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import About from "@/components/About";
import Services from "@/components/Services";
import Collections from "@/components/Collections";
import Tours from "@/components/Tours";
import Gallery from "@/components/Gallery";
import Testimonials from "@/components/Testimonials";
import Enquiry from "@/components/Enquiry";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import RoyalAssistant from "@/components/RoyalAssistant";
import TourDetail from "@/pages/TourDetail";
import Admin from "@/pages/Admin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANG_CODES = LANGS.map((l) => l.code);

const detectLang = () => {
  const saved = localStorage.getItem("ei-lang");
  if (LANG_CODES.includes(saved)) return saved;
  const param = new URLSearchParams(window.location.search).get("lang");
  if (LANG_CODES.includes(param)) return param;
  const nav = (navigator.language || "en").toLowerCase().slice(0, 2);
  return LANG_CODES.includes(nav) ? nav : "en";
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.hash.replace("#", "")).get("session_id");
    if (!sessionId) {
      navigate("/admin", { replace: true });
      return;
    }
    fetch(`${API}/auth/session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then(async (r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then((user) => {
        window.history.replaceState(null, "", "/admin");
        navigate("/admin", { state: { user }, replace: true });
      })
      .catch(() => {
        window.history.replaceState(null, "", "/admin");
        setFailed(true);
        navigate("/admin?denied=1", { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center" data-testid="auth-callback">
      <p className="font-display text-2xl text-gold-light animate-pulse">
        {failed ? "Access denied" : "Opening the royal ledger…"}
      </p>
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/tours/:id" element={<TourDetail />} />
      <Route path="/admin" element={<Admin />} />
    </Routes>
  );
}

const Home = () => {
  const [selectedTour, setSelectedTour] = useState("");
  const enquire = (tour) => {
    setSelectedTour(tour);
    document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <main className="bg-obsidian text-slate-100 min-h-screen">
      <Header />
      <Hero />
      <Marquee />
      <About />
      <Services />
      <Collections />
      <Tours onEnquire={enquire} />
      <Gallery />
      <Testimonials />
      <Enquiry selectedTour={selectedTour} />
      <Footer />
      <WhatsAppFloat />
      <RoyalAssistant />
    </main>
  );
};

function App() {
  const [lang, setLangState] = useState(detectLang);

  const setLang = (l) => {
    localStorage.setItem("ei-lang", l);
    setLangState(l);
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <LangContext.Provider value={{ lang, setLang, t: COPY[lang] }}>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <AppRouter />
        </BrowserRouter>
        <Toaster theme="dark" position="bottom-center" toastOptions={{ style: { background: "#181A24", border: "1px solid rgba(212,175,55,0.3)", color: "#F8FAFC" } }} />
      </div>
    </LangContext.Provider>
  );
}

export default App;
